import type {
  FaceMeasurementRecord,
  FaceWidthRecommendation,
  ProductDetailFaceWidthFitStatus,
} from './types';

interface ProductSizeOption {
  name?: string | null;
  value?: string | null;
}

const FRAME_SIZE_RANKS = new Map<string, number>([
  ['s', 0],
  ['narrow', 0],
  ['m', 1],
  ['medium', 1],
  ['l', 2],
  ['wide', 2],
  ['xl', 3],
  ['extrawide', 3],
]);

function resolveFrameSizeRank(value: string | null | undefined) {
  if (!value) return undefined;

  return FRAME_SIZE_RANKS.get(
    value
      .trim()
      .toLowerCase()
      .replace(/[\s_-]+/g, ''),
  );
}

export function resolveFaceWidthRecommendation(
  record: FaceMeasurementRecord | null,
  options: ProductSizeOption[],
): FaceWidthRecommendation | undefined {
  if (!record) return undefined;

  const recommendedRanks = new Set(
    record.recommendedFrameSizes
      .map(resolveFrameSizeRank)
      .filter((rank): rank is number => rank !== undefined),
  );
  const recommendation = options.reduce<FaceWidthRecommendation>(
    (result, option) => {
      const label = option.name?.trim();
      const optionId = option.value || label?.toLowerCase();
      const rank = resolveFrameSizeRank(option.value) ?? resolveFrameSizeRank(label);
      if (label && optionId && rank !== undefined && recommendedRanks.has(rank)) {
        result.labels.push(label);
        result.optionIds.push(optionId);
      }
      return result;
    },
    { labels: [], optionIds: [] },
  );

  return recommendation.labels.length > 0 && recommendation.optionIds.length > 0
    ? recommendation
    : undefined;
}

export function resolveProductDetailFaceWidthFit(
  productSize: string | null | undefined,
  recommendedFrameSizes: string[],
): ProductDetailFaceWidthFitStatus | null {
  const productRank = resolveFrameSizeRank(productSize);
  if (productRank === undefined) return null;

  const recommendedRanks = new Set(
    recommendedFrameSizes
      .map(resolveFrameSizeRank)
      .filter((rank): rank is number => rank !== undefined),
  );
  if (recommendedRanks.size === 0) return null;
  if (recommendedRanks.has(productRank)) return 'fits';

  const sortedRanks = Array.from(recommendedRanks).sort((left, right) => left - right);
  if (productRank < sortedRanks[0]) return 'too-narrow';
  if (productRank > sortedRanks[sortedRanks.length - 1]) return 'too-wide';

  return null;
}
